import React, { useState, useEffect, useRef } from 'react';
import { ClipboardList, Loader2, X, XCircle } from 'lucide-react';

// Real ingredient lists rarely exceed ~2k chars; 4k gives plenty of headroom
// while protecting the backend from accidental paste-bombs.
const MAX_MANUAL_LENGTH = 4000;

export const ManualInputPanel = ({ onAnalyze, isLoading, error, resetKey, onReset }) => {
  const [manualInput, setManualInput] = useState('');
  const [localError, setLocalError] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const textareaRef = useRef(null);

  // Clear input when resetKey changes
  useEffect(() => { setManualInput(''); setLocalError(null); setIsExpanded(false); }, [resetKey]);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea || !isExpanded) return;
    textarea.style.height = 'auto';
    textarea.style.height = `${Math.max(112, textarea.scrollHeight)}px`;
  }, [manualInput, isExpanded]);

  const handleManualCheck = () => {
    if (!isExpanded) {
      setIsExpanded(true);
      return;
    }
    if (!manualInput.trim()) {
      // Previously a silent no-op when the textarea was empty or whitespace-only;
      // users reported the app "doing nothing" with no feedback. Surface a short
      // inline hint instead so the empty click explains itself.
      setLocalError('Please enter some ingredients to analyze.');
      return;
    }
    setLocalError(null);
    onAnalyze(manualInput);
  };

  const handleClose = () => {
    if (isLoading) return;
    setManualInput('');
    setLocalError(null);
    setIsExpanded(false);
    if (typeof onReset === 'function') onReset();
  };

  const displayError = error || localError;

  const remaining = MAX_MANUAL_LENGTH - manualInput.length;
  const showCounter = manualInput.length >= MAX_MANUAL_LENGTH * 0.8;

  return (
    <>
      {!isExpanded && !isLoading && !displayError ? (
        <button
          type="button"
          onClick={() => setIsExpanded(true)}
          className="mockup-row-button mockup-row-button--amber"
        >
          <span className="mockup-row-icon mockup-row-icon--amber">
            <ClipboardList className="h-5 w-5" />
          </span>
          <span className="mockup-row-copy">
            <span className="mockup-row-title text-amber-600">Ingredient entry</span>
            <span className="mockup-row-subtitle">Type the list</span>
          </span>
          <span className="mockup-row-arrow text-orange-500">→</span>
        </button>
      ) : (
      <>
      {/* Transplanted exact JSX from App.js lines 1302-1353 (textarea + error + button only) */}
      <div className="mb-2 flex items-center justify-between gap-3">
        <label htmlFor="ingredients" className="vegai-section-label block">
          Ingredient entry
        </label>
        {!isLoading && (
          <button
            type="button"
            onClick={handleClose}
            className="inline-flex h-8 w-8 items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
            aria-label="Close ingredient entry"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
      <textarea
        ref={textareaRef}
        id="ingredients"
        value={manualInput}
        onChange={(e) => {
          setManualInput(e.target.value.slice(0, MAX_MANUAL_LENGTH));
          if (localError) setLocalError(null);
        }}
        maxLength={MAX_MANUAL_LENGTH}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey) {
            e.preventDefault();
            if (!isLoading) handleManualCheck();
          }
        }}
        placeholder="Type the list"
        className="vegai-input min-h-28 resize-none overflow-hidden p-4"
        disabled={isLoading}
      />
      {showCounter && (
        <div className={`mt-1 text-right text-xs ${remaining < 100 ? 'text-red-600' : 'text-gray-500'}`}>
          {remaining} characters remaining
        </div>
      )}
      {displayError && (
        <div className="mt-3 rounded-2xl border border-red-200 bg-red-50 p-4">
          <div className="flex items-start text-red-700">
            <XCircle className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <span className="text-sm font-medium">{displayError}</span>
              {error && (
                <div className="mt-2 text-xs text-red-600">
                  Try checking the ingredient list for missing commas, shortening extra label text, or trying again in a moment.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      <button
        onClick={handleManualCheck}
        disabled={isLoading}
        className="vegai-primary-button mt-5 w-full px-6 py-4 text-base"
      >
        {isLoading ? (
          <>
            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
            Analyzing ingredients...
          </>
        ) : (
          'Analyze ingredients'
        )}
      </button>
      </>
      )}
    </>
  );
};
